import { useNavigate, useSearchParams } from 'react-router-dom';
import {  useTranslation } from 'react-i18next';
import { APP_LINKS } from '@/helpers/links';
import { useModal } from '@/providers/modal';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useIsMobile } from '@scramble/hooks';
import { prettyMoney } from '@scramble/utils';
import { PAYMENT_STATE } from '@/helpers/cash';
import { Button } from '@scramble/ui';
import { useGetPaymentResultStatus } from '@/query/queries/cash/useGetPaymentResultStatus';
import IconSuccess from './success.svg';
import IconError from './failed.svg';
import IconProcessing from './processing.svg';
import s from './index.module.scss';

const id = MODAL_IDS.top_up_result_modal;

export const TopUpResultModal = () => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal />;
};

export const Modal = () => {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { closeModal } = useModal();

  const paymentToken = searchParams.get('payment_reference');
  const { data: result } = useGetPaymentResultStatus(paymentToken);

  const onClose = () => {
    setSearchParams({});
    closeModal(id);
  };

  if (!result) return null;

  let content;

  if (result.payment_state === PAYMENT_STATE.settled) {
    content = <PaymentSuccess amount={result.initial_amount} onClose={onClose} />
  }

  if (result.payment_state === PAYMENT_STATE.initial) {
    content = <PaymentProcessing amount={result.initial_amount} onClose={onClose} />
  }

  if (result.payment_state === PAYMENT_STATE.failed) {
    content = <PaymentError amount={result.initial_amount} onClose={onClose} />
  }

  if (!content) return null;

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalCenterLayout>
  );
};

const PaymentSuccess = ({ amount, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className={s.modal}>
      <div className={s.content}>
        <img src={IconSuccess} alt="icon" width={56} height={56} />
        <div className={s.title}>
          {t('modal:top_up_result_modal.payment_success')}
        </div>
        <div className={s.desc}>
          {t('modal:top_up_result_modal.amount_has_added', {
            amount: prettyMoney(amount),
          })}
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          label={t('modal:top_up_result_modal.add_more')}
          onClick={onClose}
        />
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          label={t('navigation:investing')}
          onClick={() => {
            navigate(APP_LINKS.investing);
            onClose();
          }}
        />
      </div>
    </div>
  );
};

const PaymentError = ({ amount, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className={s.modal}>
      <div className={s.content}>
        <img src={IconError} alt="icon" width={56} height={56} />
        <div className={s.title}>
          {t('modal:top_up_result_modal.payment_error')}
        </div>
        <div className={s.desc}>
          {t('modal:top_up_result_modal.payment_fail_desc', {
            amount: prettyMoney(amount),
          })}
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          label={t('navigation:dashboard')}
          onClick={() => {
            navigate(APP_LINKS.dashboard);
            onClose();
          }}
        />
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          label={t('modal:top_up_result_modal.try_again')}
          onClick={onClose}
        />
      </div>
    </div>
  );
};

const PaymentProcessing = ({ amount, onClose }) => {
  const { t } = useTranslation();

  return (
    <div className={s.modal}>
      <div className={s.content}>
        <img src={IconProcessing} alt="icon" width={56} height={56} />
        <div className={s.title}>
          {t('modal:top_up_result_modal.payment_processing')}
        </div>
        <div className={s.desc}>
          {t('modal:top_up_result_modal.payment_processing_desc', {
            amount: prettyMoney(amount),
          })}
        </div>
      </div>
      <div className={s.footer_single}>
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          label={t('modal:top_up_result_modal.got_it')}
          onClick={onClose}
        />
      </div>
    </div>
  );
};